import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "./ThemeToggle";
import { cn } from "@/lib/utils";
import { NAV_ITEMS } from "@/data/navigation";
import { preloadRoute } from "@/routes/registry";
import logo from "@/assets/logo-kc.png";

/** Évite la re-déclaration de l'objet à chaque render — perf micro mais propre. */
const prefetchHandlers = (path: string) => ({
  onMouseEnter: () => preloadRoute(path),
  onFocus: () => preloadRoute(path),
  onTouchStart: () => preloadRoute(path),
});

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 transition-all duration-300",
        scrolled ? "border-b border-border/60 bg-background/85 backdrop-blur-md" : "bg-transparent",
      )}
    >
      <div className="container-wide flex h-20 items-center justify-between">
        <Link to="/" className="flex items-center gap-3" aria-label="K&C Services — Accueil">
          <img src={logo} alt="K&C Services" className="h-9 w-auto" width={140} height={36} />
        </Link>

        <nav className="hidden items-center gap-10 lg:flex" aria-label="Navigation principale">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              {...prefetchHandlers(item.to)}
              className={({ isActive }) =>
                cn(
                  "relative text-sm font-medium transition-colors",
                  isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground",
                )
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <span className="absolute -bottom-2 left-0 h-px w-full bg-primary" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <ThemeToggle />
          <Button asChild size="sm" className="h-10 px-5">
            <Link to="/contact" {...prefetchHandlers("/contact")}>
              Demander un devis <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex size-10 items-center justify-center rounded-sm border border-border"
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={open}
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </div>

      <div
        className={cn(
          "overflow-hidden border-t border-border/60 bg-background transition-[max-height] duration-300 lg:hidden",
          open ? "max-h-96" : "max-h-0",
        )}
      >
        <nav className="container-wide flex flex-col py-4" aria-label="Navigation mobile">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              {...prefetchHandlers(item.to)}
              className={({ isActive }) =>
                cn(
                  "border-b border-border/40 py-3 text-base font-medium",
                  isActive ? "text-primary" : "text-foreground",
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Button asChild className="mt-4">
            <Link to="/contact" {...prefetchHandlers("/contact")}>
              Demander un devis
            </Link>
          </Button>
        </nav>
      </div>
    </header>
  );
};
